// src/components/staff/StaffTable.js
import React from 'react';
import LoadingSpinner from '../common/LoadingSpinner';

const StaffTable = ({
  staff,
  loading,
  pagination,
  onView,
  onEdit,
  onDelete,
  onPageChange,
  onExport,
  userRole
}) => {

  const handleExportClick = (format) => {
    if (format === 'pdf') {
      handlePrintPdf();
    } else {
      onExport(format);
    }
  };

  const handlePrintPdf = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) { alert('Please allow popups to print this report'); return; }

    const appLogo = localStorage.getItem('app_logo') || '';
    const apiBase = (process.env.REACT_APP_API_URL || window.location.origin || '').replace(/\/+$/, '');
    const logoUrl = (() => {
      const v = String(appLogo || '').trim();
      if (!v) return '';
      if (/^https?:\/\//i.test(v) || v.startsWith('data:image/')) return v;
      return `${apiBase}${v.startsWith('/') ? '' : '/'}${v}`;
    })();
    const esc = v => String(v ?? '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
    const fmtDate = d => d ? new Date(d).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }) : '-';

    const rows = staff.map(m => {
      const isActive = m.is_active === 1 || m.is_active === '1' || m.is_active === true;
      return `<tr>
        <td>${esc(m.name || '-')}</td>
        <td>${esc(m.staff_id || '-')}</td>
        <td>${esc(m.designation || '-')}</td>
        <td>${esc(m.email || '-')}</td>
        <td>${esc(m.phone_number || '-')}</td>
        <td>${esc(m.organization_name || '-')}</td>
        <td>${esc(m.premises_name || '-')}</td>
        <td>${fmtDate(m.date_of_appointment)}</td>
        <td>${isActive ? 'Active' : 'Inactive'}</td>
      </tr>`;
    }).join('');

    printWindow.document.write(`<!DOCTYPE html><html><head>
      <title>Staff Directory</title>
      <style>
        @page { size: A4 landscape; margin: 10mm; }
        body { font-family: Arial, sans-serif; font-size: 11px; color: #1f2937; margin: 0; padding: 8px; }
        .hdr { display:flex; justify-content:space-between; align-items:center; margin-bottom:8px; }
        .logo-img { height:44px; max-width:200px; object-fit:contain; }
        .logo-fallback { font-size:20px; font-weight:800; color:#0f172a; }
        .meta h1 { margin:0; font-size:18px; font-weight:700; color:#0f172a; text-align:right; }
        .meta p  { margin:3px 0 0; font-size:11px; color:#64748b; text-align:right; }
        hr { border:none; border-top:2px solid #e2e8f0; margin:6px 0 10px; }
        table { width:100%; border-collapse:collapse; }
        th, td { border:1px solid #e5e7eb; padding:5px 6px; text-align:left; font-size:10px; }
        th { background:#f1f5f9; font-weight:700; text-transform:uppercase; }
        tr:nth-child(even) td { background:#f9fafb; }
        .footer { margin-top:8px; font-size:10px; color:#94a3b8; text-align:right; }
      </style>
    </head><body>
      <div class="hdr">
        <div>${logoUrl ? `<img class="logo-img" src="${esc(logoUrl)}" alt="Logo" onerror="this.style.display='none'" />` : '<div class="logo-fallback">eHAAZRI</div>'}</div>
        <div class="meta">
          <h1>Staff Directory</h1>
          <p>Generated: ${new Date().toLocaleDateString('en-GB', { day:'2-digit', month:'long', year:'numeric' })} &nbsp;·&nbsp; ${staff.length} records</p>
        </div>
      </div>
      <hr />
      <table>
        <thead><tr><th>Name</th><th>Staff ID</th><th>Designation</th><th>Email</th><th>Phone</th><th>Organization</th><th>Premises</th><th>Join Date</th><th>Status</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
      <div class="footer">eHAAZRI Attendance Management System</div>
    </body></html>`);
    printWindow.document.close();
    printWindow.focus();
    const safePrint = () => setTimeout(() => { printWindow.print(); printWindow.close(); }, 200);
    printWindow.onload = () => {
      const img = printWindow.document.querySelector('.logo-img');
      if (!img) return safePrint();
      if (img.complete && img.naturalWidth > 0) return safePrint();
      let done = false;
      const fin = () => { if (done) return; done = true; safePrint(); };
      img.onload = fin; img.onerror = fin;
      setTimeout(fin, 1200);
    };
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map(n => n.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;

    // If the path is already a full URL, return it as-is
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }

    // Use the API base URL directly (keep /api in the path)
    const apiUrl = process.env.REACT_APP_API_URL;

    // Ensure the path starts with /
    const cleanPath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;

    return `${apiUrl}${cleanPath}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (member) => {
    const isActive =
      member?.is_active === 1 ||
      member?.is_active === '1' ||
      member?.is_active === true;
    const normalizedStatus = (member?.status || '').toString().toLowerCase();

    let statusInfo = { class: 'inactive', text: 'Disabled' };
    if (normalizedStatus === 'on_leave') {
      statusInfo = { class: 'on-leave', text: 'On Leave' };
    } else if (isActive || normalizedStatus === 'active') {
      statusInfo = { class: 'active', text: 'Active' };
    } else if (normalizedStatus === 'inactive' || normalizedStatus === 'disabled') {
      statusInfo = { class: 'inactive', text: 'Disabled' };
    }

    return (
      <span className={`status-badge ${statusInfo.class}`}>
        {statusInfo.text}
      </span>
    );
  };

  const getUserTypeBadge = (userType) => {
    const userTypeMap = {
      core_admin: { class: 'core-admin', text: 'Core Admin' },
      client_admin: { class: 'client-admin', text: 'Client Admin' },
      app_admin: { class: 'app-admin', text: 'App Admin' },
      staff: { class: 'staff', text: 'Staff' }
    };

    const typeInfo = userTypeMap[userType] || { class: 'staff', text: 'Staff' };
    return (
      <span className={`user-type-badge ${typeInfo.class}`}>
        {typeInfo.text}
      </span>
    );
  };

  const renderPaginationButtons = () => {
    const buttons = [];
    const { current_page, total_pages } = pagination;

    // Previous button
    buttons.push(
      <button
        key="prev"
        className="pagination-btn"
        onClick={() => onPageChange(current_page - 1)}
        disabled={current_page <= 1}
      >
        <i className="fas fa-chevron-left"></i>
      </button>
    );

    // Page numbers
    const startPage = Math.max(1, current_page - 2);
    const endPage = Math.min(total_pages, current_page + 2);

    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <button
          key={i}
          className={`pagination-btn ${i === current_page ? 'active' : ''}`}
          onClick={() => onPageChange(i)}
        >
          {i}
        </button>
      );
    }

    // Next button
    buttons.push(
      <button
        key="next"
        className="pagination-btn"
        onClick={() => onPageChange(current_page + 1)}
        disabled={current_page >= total_pages}
      >
        <i className="fas fa-chevron-right"></i>
      </button>
    );

    return buttons;
  };

  return (
    <div className="staff-table-container">
      <div className="table-header">
        <div className="table-title">Staff Directory</div>
        <div className="table-actions">
          <div className="export-dropdown">
            <button className="btn btn-secondary btn-sm dropdown-toggle">
              <i className="fas fa-download"></i>
              Export
            </button>
            <div className="dropdown-menu">
              <button onClick={() => handleExportClick('csv')} className="dropdown-item">
                <i className="fas fa-file-csv"></i> CSV
              </button>
              <button onClick={() => handleExportClick('excel')} className="dropdown-item">
                <i className="fas fa-file-excel"></i> Excel
              </button>
              <button onClick={() => handleExportClick('pdf')} className="dropdown-item">
                <i className="fas fa-file-pdf"></i> PDF
              </button>
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="table-loading">
          <LoadingSpinner />
          <p>Loading staff data...</p>
        </div>
      ) : staff.length === 0 ? (
        <div className="empty-state">
          <i className="fas fa-users empty-icon"></i>
          <h3>No Staff Members Found</h3>
          <p>No staff members match your current filters. Try adjusting your search criteria.</p>
        </div>
      ) : (
        <>
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th>Staff Member</th>
                  <th>Staff ID</th>
                  <th>Designation</th>
                  <th>User Type</th>
                  <th>Organization</th>
                  <th>Premises</th>
                  <th>Contact</th>
                  <th>Join Date</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {staff.map((member) => (
                  <tr key={member.id}>
                    <td>
                      <div className="staff-info">
                        <div className="staff-avatar">
                          {member.image ? (
                            <>
                              <img
                                src={getImageUrl(member.image)}
                                alt={member.name}
                                onError={(e) => {
                                  // Fallback to initials if image fails to load
                                  console.log('Image failed to load:', getImageUrl(member.image));
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                              <div
                                className="avatar-initials"
                                style={{ display: 'none' }}
                              >
                                {getInitials(member.name)}
                              </div>
                            </>
                          ) : (
                            <div className="avatar-initials">
                              {getInitials(member.name)}
                            </div>
                          )}
                        </div>
                        <div className="staff-details">
                          <h4>{member.name}</h4>
                          <p>{member.email || 'No email'}</p>
                        </div>
                      </div>
                    </td>
                    <td><strong>{member.staff_id}</strong></td>
                    <td>{member.designation || '-'}</td>
                    <td>{getUserTypeBadge(member.user_type)}</td>
                    <td>{member.organization_name || '-'}</td>
                    <td>{member.premises_name || '-'}</td>
                    <td>{member.phone_number || '-'}</td>
                    <td>{formatDate(member.date_of_appointment)}</td>
                    <td>{getStatusBadge(member)}</td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="action-btn view"
                          onClick={() => onView(member)}
                          title="View Details"
                        >
                          <i className="fas fa-eye"></i>
                        </button>
                        <button
                          className="action-btn edit"
                          onClick={() => onEdit(member)}
                          title="Edit Staff"
                        >
                          <i className="fas fa-edit"></i>
                        </button>
                        {userRole === 'core_admin' && (
                          <button
                            className="action-btn delete"
                            onClick={() => onDelete(member)}
                            title="Delete Staff"
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="table-footer">
            <div className="pagination-info">
              Showing {((pagination.current_page - 1) * pagination.per_page) + 1} to {Math.min(pagination.current_page * pagination.per_page, pagination.total)} of {pagination.total} results
            </div>

            <div className="pagination">
              {renderPaginationButtons()}
            </div>
          </div>
        </>
      )}

      <style jsx>{`
        .staff-table-container {
          background: var(--white);
          border-radius: var(--radius-xl);
          box-shadow: var(--shadow-md);
          overflow: hidden;
        }

        .table-header {
          background: var(--admin-gradient);
          color: var(--white);
          padding: var(--spacing-lg);
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .table-title {
          font-size: 1.3rem;
          font-weight: 600;
        }

        .table-actions {
          display: flex;
          gap: var(--spacing-sm);
        }

        .export-dropdown {
          position: relative;
        }

        .dropdown-toggle::after {
          content: '';
          margin-left: 0.5rem;
        }

        .dropdown-menu {
          position: absolute;
          top: 100%;
          right: 0;
          background: var(--white);
          border-radius: var(--radius-md);
          box-shadow: var(--shadow-lg);
          border: 1px solid var(--light-color);
          margin-top: 0.25rem;
          min-width: 150px;
          opacity: 0;
          visibility: hidden;
          transform: translateY(-10px);
          transition: all var(--transition-normal);
          z-index: 100;
        }

        .export-dropdown:hover .dropdown-menu {
          opacity: 1;
          visibility: visible;
          transform: translateY(0);
        }

        .dropdown-item {
          display: flex;
          align-items: center;
          gap: var(--spacing-sm);
          padding: var(--spacing-sm) var(--spacing-md);
          background: none;
          border: none;
          width: 100%;
          text-align: left;
          color: var(--text-dark);
          font-size: 0.9rem;
          cursor: pointer;
          transition: background var(--transition-fast);
        }

        .dropdown-item:hover {
          background: var(--light-color);
        }

        .table-loading {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: var(--spacing-xxl);
          gap: var(--spacing-md);
          color: var(--text-light);
        }

        .empty-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: var(--spacing-xxl);
          text-align: center;
          color: var(--text-light);
        }

        .empty-icon {
          font-size: 4rem;
          margin-bottom: var(--spacing-lg);
          opacity: 0.5;
        }

        .empty-state h3 {
          margin-bottom: var(--spacing-sm);
          color: var(--text-dark);
        }

        .table-responsive {
          max-height: min(70vh, 720px);
          overflow: auto;
          scrollbar-gutter: stable both-edges;
          -webkit-overflow-scrolling: touch;
        }

        .table {
          width: max-content;
          min-width: 1250px;
          border-collapse: collapse;
        }

        .table th {
          background: var(--light-color);
          color: var(--text-dark);
          font-weight: 600;
          padding: var(--spacing-md);
          text-align: left;
          border-bottom: 2px solid #bdc3c7;
          white-space: nowrap;
          position: sticky;
          top: 0;
          z-index: 2;
        }

        .table td {
          padding: var(--spacing-md);
          border-bottom: 1px solid #ecf0f1;
          vertical-align: middle;
        }

        .table tr:hover {
          background: rgba(52, 152, 219, 0.05);
        }

        .staff-info {
          display: flex;
          align-items: center;
          gap: var(--spacing-md);
        }

        .staff-avatar {
          width: 50px;
          height: 50px;
          border-radius: 50%;
          background: var(--admin-gradient);
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--white);
          font-weight: bold;
          font-size: 1.2rem;
          overflow: hidden;
          flex-shrink: 0;
        }

        .staff-avatar img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .staff-details h4 {
          font-weight: 600;
          margin-bottom: 0.2rem;
          color: var(--text-dark);
          margin: 0 0 0.2rem 0;
        }

        .staff-details p {
          color: var(--text-light);
          font-size: 0.9rem;
          margin: 0;
        }

        .action-buttons {
          display: flex;
          gap: var(--spacing-sm);
        }

        .action-btn {
          padding: 0.4rem 0.8rem;
          border: none;
          border-radius: var(--radius-sm);
          font-size: 0.8rem;
          cursor: pointer;
          transition: all var(--transition-normal);
          display: flex;
          align-items: center;
          gap: 0.3rem;
        }

        .action-btn.view {
          background: rgba(52, 152, 219, 0.1);
          color: var(--info);
          border: 1px solid var(--info);
        }

        .action-btn.edit {
          background: rgba(243, 156, 18, 0.1);
          color: var(--warning);
          border: 1px solid var(--warning);
        }

        .action-btn.delete {
          background: rgba(231, 76, 60, 0.1);
          color: var(--error);
          border: 1px solid var(--error);
        }

        .action-btn:hover {
          transform: scale(1.05);
        }

        .table-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: var(--spacing-lg);
          background: var(--light-color);
        }

        .pagination-info {
          color: var(--text-light);
          font-size: 0.9rem;
        }

        .pagination {
          display: flex;
          gap: var(--spacing-sm);
        }

        .pagination-btn {
          padding: var(--spacing-sm) var(--spacing-md);
          border: 1px solid #bdc3c7;
          background: var(--white);
          color: var(--text-dark);
          border-radius: var(--radius-sm);
          cursor: pointer;
          transition: all var(--transition-normal);
          min-width: 35px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .pagination-btn:hover:not(.active):not(:disabled) {
          background: var(--light-color);
        }

        .pagination-btn.active {
          background: var(--admin-gradient);
          color: var(--white);
          border-color: transparent;
        }

        .pagination-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        @media (max-width: 768px) {
          .table-header {
            flex-direction: column;
            gap: var(--spacing-md);
            text-align: center;
          }

          .table-actions {
            justify-content: center;
          }

          .table-responsive {
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
          }

          .table {
            min-width: 800px;
          }

          .table th,
          .table td {
            padding: var(--spacing-sm);
            font-size: 0.9rem;
          }

          .staff-info {
            flex-direction: column;
            align-items: flex-start;
            gap: var(--spacing-sm);
          }

          .action-buttons {
            flex-direction: column;
          }

          .table-footer {
            flex-direction: column;
            gap: var(--spacing-md);
          }

          .pagination {
            justify-content: center;
          }
        }

        .staff-avatar {
    position: relative;
    width: 50px;
    height: 50px;
    border-radius: 50%;
    background: var(--admin-gradient);
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--white);
    font-weight: bold;
    font-size: 1.2rem;
    overflow: hidden;
    flex-shrink: 0;
}

.staff-avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 50%;
}

.avatar-initials {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--admin-gradient);
    color: var(--white);
    font-weight: bold;
    font-size: 1.2rem;
    border-radius: 50%;
}

.status-badge {
    padding: 0.25rem 0.75rem;
    border-radius: 12px;
    font-size: 0.85rem;
    font-weight: 500;
    display: inline-block;
}

.status-badge.active {
    background: rgba(46, 204, 113, 0.1);
    color: #27ae60;
}

.status-badge.inactive {
    background: rgba(231, 76, 60, 0.1);
    color: #e74c3c;
}

.status-badge.on-leave {
    background: rgba(243, 156, 18, 0.1);
    color: #f39c12;
}

.user-type-badge {
    padding: 0.25rem 0.75rem;
    border-radius: 12px;
    font-size: 0.85rem;
    font-weight: 500;
    display: inline-block;
}

.user-type-badge.core-admin {
    background: rgba(155, 89, 182, 0.1);
    color: #8e44ad;
    border: 1px solid #8e44ad;
}

.user-type-badge.client-admin {
    background: rgba(52, 152, 219, 0.1);
    color: #2980b9;
    border: 1px solid #2980b9;
}

.user-type-badge.app-admin {
    background: rgba(46, 204, 113, 0.1);
    color: #27ae60;
    border: 1px solid #27ae60;
}

.user-type-badge.staff {
    background: rgba(149, 165, 166, 0.1);
    color: #7f8c8d;
    border: 1px solid #95a5a6;
}
      `}</style>
    </div>
  );
};

export default StaffTable;
